import { useState } from "react";
import type { FormEvent } from "react";
import { unwrap, useAdminSession, useApi, useResource } from "@backcoach/ui";

/**
 * Admin colleagues: invite another org admin by email, remove one. Never a
 * participant. An invited address signs in by magic link from then on.
 */
export function AdminsScreen({ orgId }: { orgId: string }) {
  const api = useApi();
  const session = useAdminSession();
  const admins = useResource(async (a) => unwrap(await a.GET("/v1/orgs/{orgId}/admins", { params: { path: { orgId } } })), [orgId]);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<string | null>(null);

  async function invite(e: FormEvent) {
    e.preventDefault();
    setNote(null);
    const r = await api.POST("/v1/orgs/{orgId}/admins", { params: { path: { orgId } }, body: { email } });
    if (r.data) {
      setNote(`${r.data.email} can sign in to the console by magic link now.`);
      setEmail("");
      admins.reload();
    } else {
      setNote(r.error?.message ?? "Could not invite that address");
    }
  }

  async function remove(adminId: string) {
    const r = await api.DELETE("/v1/orgs/{orgId}/admins/{adminId}", { params: { path: { orgId, adminId } } });
    if (r.response.ok) admins.reload();
    else setNote(r.error?.message ?? "Could not remove that admin");
  }

  return (
    <div className="bc-screen bc-settings">
      <header className="bc-head">
        <p className="bc-kicker">Organisation</p>
        <h1>Admin colleagues</h1>
        <p className="bc-sub">People who can see this dashboard and change these settings. Nobody here is a participant.</p>
      </header>

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>Admins</h2>
        </div>
        <ul className="org-list">
          {(admins.data ?? []).map((a) => (
            <li key={a.id} className="org-row">
              <div className="org-main">
                <div className="org-title">
                  <b>{a.email}</b>
                  <span className="bc-chip bc-chip-quiet">{a.role === "platform_admin" ? "Back Coach" : "org admin"}</span>
                </div>
              </div>
              {a.id !== session.data?.userId && a.role === "org_admin" && (
                <div className="org-actions">
                  <button type="button" className="bc-btn bc-btn-quiet" onClick={() => void remove(a.id)}>
                    Remove
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      <form className="bc-block" onSubmit={(e) => void invite(e)}>
        <div className="bc-block-head">
          <h2>Invite a colleague</h2>
        </div>
        <div className="set-row">
          <span className="set-label">Work email</span>
          <span className="set-control">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <button type="submit" className="bc-btn">
              Invite
            </button>
          </span>
        </div>
        {note && <p className="bc-note">{note}</p>}
      </form>
    </div>
  );
}
